import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Button,
    CircularProgress,
    makeStyles,
} from '@material-ui/core';
import { SESSION_TIMEOUT_CONFIG } from 'shared/constants/session-timeout';

interface ISessionTimeoutModalProps {
    open: boolean;
    remainingSeconds: number;
    onStayLoggedIn: () => void;
    onLogoutNow: () => void;
}

const useStyles = makeStyles((theme) => ({
    paper: {
        width: 440,
        maxWidth: '95%',
        borderRadius: 8,
        padding: theme.spacing(3, 2),
        overflow: 'hidden',
        background: '#FFFFFF',
        border: '1px solid #D7E3F0',
        boxShadow: '0 14px 40px rgba(15,23,42,.18)',
        position: 'relative',

        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: 4,
            background: '#18A7A8',
        },
    },

    title: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: 0,
    },

    titleText: {
        fontWeight: 700,
        fontSize: '1.6rem',
        color: '#1A2942',
        letterSpacing: '-0.01em',
    },

    content: {
        padding: theme.spacing(0, 2, 1),
        textAlign: 'center',
    },

    description: {
        color: '#5F6C7B',
        fontSize: '0.96rem',
        lineHeight: 1.6,
        fontWeight: 400,
        padding: theme.spacing(0, 1),
    },

    timerContainer: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: theme.spacing(4, 0),
        width: 165,
        height: 165,
        borderRadius: '50%',
        background: '#FAFCFE',
    },

    timerBackgroundProgress: {
        color: '#E6EDF5',
        position: 'absolute',
        transform: 'rotate(-90deg)',
    },

    timerActiveProgress: {
        color: 'rgb(2, 116, 209)',
        position: 'absolute',
        transform: 'rotate(-90deg)',
        transition: 'all .3s linear',
    },

    timer: {
        fontWeight: 500,
        paddingTop: 12,
        fontSize: '2.0rem',
        color: '#1A2942',
        letterSpacing: '0.02em',
    },

    subtitle: {
        display: 'none',
    },

    actions: {
        padding: theme.spacing(1, 2, 1),
        justifyContent: 'center',
        gap: theme.spacing(2),
    },

    stayActiveBtn: {
        borderRadius: 4,
        padding: theme.spacing(1.5, 3),
        minWidth: 170,
        textTransform: 'none',
        fontSize: '0.95rem',
        fontWeight: 600,
        background: '#1A2942',
        color: '#FFFFFF',
        boxShadow: 'none',

        '&:hover': {
            background: '#122036',
            boxShadow: 'none',
        },
    },

    logoutBtn: {
        borderRadius: 4,
        padding: theme.spacing(1.5, 3),
        minWidth: 120,
        textTransform: 'none',
        fontSize: '0.95rem',
        fontWeight: 600,
        background: '#F4F7FA',
        color: '#1A2942',
        border: '1px solid #D7E3F0',
        boxShadow: 'none',

        '&:hover': {
            background: '#E9EEF5',
            boxShadow: 'none',
        },
    },
}));

const SessionTimeoutModal: React.FC<ISessionTimeoutModalProps> = ({
    open,
    remainingSeconds,
    onStayLoggedIn,
    onLogoutNow,
}) => {
    const classes = useStyles();

    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    // Dynamic countdown ring percentage calculations (assumes 60s total fallback threshold display)
    const progressValue = Math.max(
        0,
        Math.min(((remainingSeconds * 1000) / SESSION_TIMEOUT_CONFIG.WARNING_BEFORE_TIMEOUT_MS) * 100, 100),
    );
    return (
        <Dialog
            open={open}
            disableBackdropClick
            disableEscapeKeyDown
            fullWidth
            maxWidth="xs"
            classes={{ paper: classes.paper }}
        >
            <DialogTitle disableTypography className={classes.title}>
                <Typography className={classes.titleText}>Session timeout</Typography>
            </DialogTitle>

            <DialogContent className={classes.content}>
                <Typography className={classes.description}>
                    You haven&apos;t used your login session for a while - so for security, we&apos;ll log you out soon
                </Typography>

                <Box className={classes.timerContainer}>
                    <CircularProgress
                        variant="determinate"
                        className={classes.timerBackgroundProgress}
                        size={150}
                        thickness={6}
                        value={100}
                    />
                    <CircularProgress
                        variant="determinate"
                        className={classes.timerActiveProgress}
                        size={150}
                        thickness={6}
                        value={progressValue}
                    />
                    <Typography className={classes.timer}>
                        {`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`}
                    </Typography>
                </Box>

                <Typography className={classes.subtitle}>Any unsaved change progress will be lost.</Typography>
            </DialogContent>

            <DialogActions className={classes.actions}>
                <Button onClick={onStayLoggedIn} variant="contained" className={classes.stayActiveBtn} autoFocus>
                    Keep me logged in
                </Button>

                <Button onClick={onLogoutNow} variant="contained" className={classes.logoutBtn}>
                    Log out &gt;
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionTimeoutModal;
